// src/components/HelprrBodyExercise.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../css2/HelprrBodyExercise.css";

/**
 * HelprrBodyExercise (Calm-level, auto-timed)
 * - No "Continue" button
 * - Steps advance automatically based on duration
 * - Circular progress ring shows gentle progress (no %)
 * - Pause/Resume supported
 * - Optional subtle Skip →
 */
export default function HelprrBodyExercise({
  title = "Micro body release",
  intro = "Let the body soften before the mind tries to fix anything.",
  durationLabel = "~1 minute • optional",
  mode = "warm", // warm | cool | steady (CSS variants)
  illustrationSrc = "/assets/illustrations/helprr-breathing.png", // your transparent PNG
  steps = [
    { text: "If it feels okay, let your shoulders drop just a little.", hint: "Even 5–10% is enough.", ms: 15000 },
    { text: "Let your jaw loosen.", hint: "Teeth don’t need to touch.", ms: 15000 },
    { text: "Gently press your feet into the floor.", hint: "Notice what’s holding you.", ms: 15000 },
    { text: "Take one slow breath out.", hint: "Longer out if it feels natural.", ms: 15000 },
  ],
  endNote = "Even small releases tell your nervous system it’s safe.",
  autoStart = true,
  showSkip = true,
  onComplete,
}) {
  const totalMs = useMemo(() => {
    const sum = steps.reduce((acc, s) => acc + (s.ms || 0), 0);
    return sum > 0 ? sum : 60000;
  }, [steps]);

  const schedule = useMemo(() => {
    // Build step time ranges [start, end)
    let t = 0;
    return steps.map((s) => {
      const ms = s.ms || Math.round(totalMs / Math.max(steps.length, 1));
      const start = t;
      const end = t + ms;
      t = end;
      return { ...s, start, end, ms };
    });
  }, [steps, totalMs]);

  const [isPlaying, setIsPlaying] = useState(autoStart);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [phaseKey, setPhaseKey] = useState(0); // forces fade-in when step changes

  const rafRef = useRef(null);
  const lastTsRef = useRef(null);
  const doneRef = useRef(false);

  const progress = Math.min(elapsedMs / totalMs, 1);

  const activeStepIndex = useMemo(() => {
    const idx = schedule.findIndex((s) => elapsedMs >= s.start && elapsedMs < s.end);
    if (idx === -1) return progress >= 1 ? schedule.length : 0;
    return idx;
  }, [elapsedMs, schedule, progress]);

  const activeStep = schedule[activeStepIndex];

  useEffect(() => {
    setIsPlaying(autoStart);
  }, [autoStart]);

  // Main timer loop (pause/resume safe)
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
      return;
    }

    const tick = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const delta = ts - lastTsRef.current;
      lastTsRef.current = ts;

      setElapsedMs((prev) => {
        const next = Math.min(prev + delta, totalMs);
        return next;
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [isPlaying, totalMs]);

  // Trigger fade-in when step changes
  useEffect(() => {
    setPhaseKey((k) => k + 1);
  }, [activeStepIndex]);

  // Completion callback (once)
  useEffect(() => {
    if (progress < 1) return;
    if (doneRef.current) return;
    doneRef.current = true;
    setIsPlaying(false);
    onComplete?.();
  }, [progress, onComplete]);

  const handleRestart = () => {
    doneRef.current = false;
    setElapsedMs(0);
    setIsPlaying(true);
  };

  const handleSkip = () => {
    if (!activeStep) return;
    const nextIndex = Math.min(activeStepIndex + 1, schedule.length);
    if (nextIndex >= schedule.length) {
      setElapsedMs(totalMs);
      return;
    }
    setElapsedMs(schedule[nextIndex].start);
  };

  // SVG ring geometry
  const R = 56;
  const C = 2 * Math.PI * R;
  const dashOffset = C * (1 - progress);

  return (
    <section className={`hbe-card hbe--${mode}`} aria-label="Body-based exercise">
      <header className="hbe-head">
        <div className="hbe-titleWrap">
          <h3 className="hbe-title">{title}</h3>
          <div className="hbe-meta">{durationLabel}</div>
        </div>

        <div className="hbe-controls">
          <button
            type="button"
            className="hbe-pill"
            onClick={() => setIsPlaying((v) => !v)}
            aria-label={isPlaying ? "Pause exercise" : "Resume exercise"}
          >
            {isPlaying ? "Pause" : "Resume"}
          </button>

          <button
            type="button"
            className="hbe-pill hbe-pill--ghost"
            onClick={handleRestart}
            aria-label="Restart exercise"
          >
            Restart
          </button>
        </div>
      </header>

      <p className="hbe-intro">{intro}</p>

      <div className="hbe-stage">
        <div className="hbe-visual" aria-hidden="true">
          {/* soft glow */}
          <div className="hbe-glow" />

          {/* ring progress */}
          <svg className="hbe-ring" viewBox="0 0 140 140" role="img" aria-label="Progress">
            <circle className="hbe-ringTrack" cx="70" cy="70" r={R} />
            <circle
              className="hbe-ringProg"
              cx="70"
              cy="70"
              r={R}
              strokeDasharray={C}
              strokeDashoffset={dashOffset}
            />
          </svg>

          {/* illustration */}
          <img
            className="hbe-illus"
            src={illustrationSrc}
            alt=""
            draggable="false"
            loading="lazy"
          />
        </div>

        <div className="hbe-script" aria-live="polite">
          {progress < 1 && activeStep ? (
            <div key={phaseKey} className="hbe-stepFade">
              <div className="hbe-guidance">Moving at your pace</div>

              <div className="hbe-stepText">{activeStep.text}</div>
              {activeStep.hint ? <div className="hbe-stepHint">{activeStep.hint}</div> : null}

              {showSkip ? (
                <button type="button" className="hbe-skip" onClick={handleSkip} aria-label="Skip to next cue">
                  Next <span aria-hidden="true">→</span>
                </button>
              ) : null}
            </div>
          ) : (
            <div className="hbe-end">
              <div className="hbe-endTitle">That’s enough.</div>
              <div className="hbe-endText">{endNote}</div>
            </div>
          )}
        </div>
      </div>

      <footer className="hbe-foot">
        You can stop anytime. Even a few breaths count.
      </footer>
    </section>
  );
}
